import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from '@/components/ui/accordion'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import useUploadFile from '@/hooks/useUploadFile'
import DeleteOutlineIcons from '@/icons/DeleteOutlineIcons'
import {
    DNS_MANAGEMENT_TYPES,
    INTERNAL_SERVICE_STORAGE_PREFIX_URLS,
} from '@/modules/Service/constants'
import {
    createServiceNotification,
    updateServiceNotification,
} from '@/modules/Service/services'
import {
    DNSManagementType,
    ServiceNotification,
    ServiceNotificationProps,
} from '@/modules/Service/types'
import { TechnicalTemplate } from '@/modules/Settings/types'
import { Attachment } from '@/modules/Upload/types'
import { omit, renderFileItem } from '@/utils/function'
import { yupResolver } from '@hookform/resolvers/yup'
import { FileText, Globe, Key, Paperclip, Plus, Save, X } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FormItem, GridHookForm, IconButton } from 'vnetwork-ui'
import { serviceInformationSchema } from './schema'
import TemplateSelector from './TemplateSelector'

export type ServiceInformationType = {
    notification_name: string
    dns_enable: boolean
    dns_managements: Array<{
        name: string
        type: DNSManagementType
        target: string
    }>
    metadata: Array<{ key: string; value: string }>
    documentations: Array<{ name: string; link: string }>
}

// Quick-add metadata presets
const METADATA_PRESETS = [
    { key: 'USER', label: 'User' },
    { key: 'PASS', label: 'Pass' },
    { key: 'LINK', label: 'Link' },
    { key: 'IP', label: 'IP' },
    { key: 'PORT', label: 'Port' },
    { key: 'DB_NAME', label: 'DB' },
] as const

interface CreateOrEditProps extends ServiceNotificationProps {
    editData?: ServiceNotification | null
    isDrawer?: boolean
}

const Create: React.FC<CreateOrEditProps> = ({
    serviceID,
    onClose,
    productGroupName,
    editData,
}) => {
    const { t } = useTranslation()
    const { t: tService } = useTranslation('services')
    const isEditMode = Boolean(editData)

    const [templateAttachments, setTemplateAttachments] = useState<
        Attachment[]
    >(editData?.attachments || [])

    useEffect(() => {
        setTemplateAttachments(editData?.attachments || [])
    }, [editData])

    const defaultValues = editData
        ? {
              notification_name: editData.notification_name,
              dns_enable: editData.dns_enable,
              dns_managements:
                  editData.dns_managements?.map((dns) => ({
                      name: dns.name,
                      type: dns.type as DNSManagementType,
                      target: dns.target,
                  })) || [],
              metadata: editData.metadata || [],
              documentations: editData.documentations || [],
              attachments: [] as File[],
          }
        : {
              notification_name: 'Cấu hình dịch vụ & tài khoản đăng nhập',
              dns_enable: true,
              dns_managements: [
                  { name: '', type: 'cname' as DNSManagementType, target: '' },
              ],
              metadata: [
                  { key: 'USER', value: '' },
                  { key: 'PASS', value: '' },
                  { key: 'LINK', value: '' },
              ],
              documentations: [{ name: '', link: '' }],
              attachments: [] as File[],
          }

    const formControl = useForm<
        ServiceInformationType & { attachments: File[] }
    >({
        defaultValues,
        resolver: yupResolver(serviceInformationSchema as any),
    })

    const {
        control,
        handleSubmit,
        watch,
        formState: { errors },
        setValue,
        reset,
    } = formControl

    useEffect(() => {
        reset(defaultValues)
    }, [editData, reset])

    const {
        fields: dnsFields,
        remove: removeDns,
        append: appendDns,
        replace: replaceDns,
    } = useFieldArray({ name: 'dns_managements', control })
    const {
        fields: metaFields,
        remove: removeMeta,
        append: appendMeta,
        replace: replaceMeta,
    } = useFieldArray({ name: 'metadata', control })
    const {
        fields: docFields,
        remove: removeDoc,
        append: appendDoc,
        replace: replaceDocs,
    } = useFieldArray({ name: 'documentations', control })

    const { uploadAsync, isUploading } = useUploadFile({
        prefix: INTERNAL_SERVICE_STORAGE_PREFIX_URLS.Notifications,
    })

    const handleTemplateSelect = (template: TechnicalTemplate | null) => {
        if (!template) {
            setTemplateAttachments([])
            return
        }
        if (template.template_name)
            setValue('notification_name', template.template_name)
        setValue('dns_enable', template.dns_enable)
        replaceDns(
            template.dns_managements?.map((d) => ({
                name: d.name,
                type: d.type as DNSManagementType,
                target: d.value,
            })) || []
        )
        replaceMeta(
            template.metadata?.map((m) => ({ key: m.key, value: m.value })) ||
                []
        )
        replaceDocs(
            template.documentations?.map((d) => ({
                name: d.title,
                link: d.url,
            })) || []
        )
        setTemplateAttachments(
            template.attachments?.length ? template.attachments : []
        )
    }

    const onSubmit = async (
        values: ServiceInformationType & { attachments: File[] }
    ) => {
        let newAttachments: Attachment[] = []
        if (values?.attachments?.length) {
            const uploaded = await uploadAsync(values.attachments)
            newAttachments = uploaded
                ?.filter((a) => a.status === 'fulfilled')
                .map((a) => a.value) as Attachment[]
        }
        const allAttachments = [...templateAttachments, ...newAttachments]
        if (isEditMode && editData) {
            await updateServiceNotification({
                serviceID,
                notificationID: editData.service_system_information_id,
                attachments: allAttachments,
                data: omit(values, ['attachments']),
            })
        } else {
            await createServiceNotification({
                serviceID,
                attachments: allAttachments,
                data: omit(values, ['attachments']),
            })
        }
    }

    const { mutateAsync, isPending } = useMutationWithHandler(
        { mutationFn: onSubmit },
        {
            successMessage: isEditMode
                ? t('update-service-notification-success')
                : t('create-service-notification-success'),
            reactQueryKeys: QUERY_KEYS.SERVICES_NOTIFICATIONS,
            onSuccess: () => onClose(),
        }
    )

    const dnsEnabled = watch('dns_enable')
    const isLoading = isUploading || isPending

    const existingMetaKeys = metaFields.map((f) =>
        ((f as any).key || '').toUpperCase()
    )
    const availablePresets = METADATA_PRESETS.filter(
        (p) => !existingMetaKeys.includes(p.key)
    )

    return (
        <form
            onSubmit={handleSubmit((values) => mutateAsync(values as any))}
            className="space-y-5 pt-4"
        >
            {/* Template Selector */}
            <TemplateSelector
                formControl={formControl}
                productGroupName={productGroupName}
                onTemplateSelect={handleTemplateSelect}
            />

            {/* Notification Name + DNS toggle */}
            <div className="space-y-3">
                <FormItem
                    name="notification_name"
                    type="textfield"
                    control={control}
                    label={t('notification_name')}
                    placeholder={t('notification_name')}
                />
                {errors.notification_name && (
                    <p className="text-xs text-destructive">
                        {errors.notification_name.message}
                    </p>
                )}
                <FormItem
                    name="dns_enable"
                    type="checkbox"
                    control={control}
                    label={t('dns_enable')}
                />
            </div>

            {/* Accordion Sections */}
            <Accordion
                type="multiple"
                defaultValue={[
                    ...(dnsEnabled ? ['dns'] : []),
                    'metadata',
                    'documentation',
                ]}
                className="space-y-2"
            >
                {/* DNS Management */}
                {dnsEnabled && (
                    <AccordionItem
                        value="dns"
                        className="border rounded-lg px-4 bg-card"
                    >
                        <AccordionTrigger className="py-3 hover:no-underline">
                            <div className="flex items-center gap-2">
                                <Globe className="h-4 w-4 text-blue-500" />
                                <span className="font-medium text-sm">
                                    DNS Management
                                </span>
                                {dnsFields.length > 0 && (
                                    <Badge
                                        variant="outline-primary"
                                        className="h-5 px-1.5 text-xs"
                                    >
                                        {dnsFields.length}
                                    </Badge>
                                )}
                            </div>
                        </AccordionTrigger>
                        <AccordionContent>
                            <div className="space-y-3 pb-1">
                                {(errors.dns_managements as any)?.root
                                    ?.message && (
                                    <p className="text-xs text-destructive">
                                        {t(
                                            (errors.dns_managements as any).root
                                                .message
                                        )}
                                    </p>
                                )}
                                {dnsFields.map((field, index) => (
                                    <div
                                        key={field.id}
                                        className="grid grid-cols-12 gap-2 items-start"
                                    >
                                        <div className="col-span-3">
                                            <FormItem
                                                name={`dns_managements.${index}.name`}
                                                type="textfield"
                                                control={control}
                                                label="Name"
                                                placeholder="@ / www / *"
                                                size="small"
                                            />
                                        </div>
                                        <div className="col-span-3">
                                            <FormItem
                                                name={`dns_managements.${index}.type`}
                                                type="select"
                                                control={control}
                                                label="Type"
                                                size="small"
                                                allowClear={false}
                                                options={DNS_MANAGEMENT_TYPES}
                                            />
                                        </div>
                                        <div className="col-span-5">
                                            <FormItem
                                                name={`dns_managements.${index}.target`}
                                                type="textfield"
                                                control={control}
                                                label="Target"
                                                placeholder="IP / CNAME target"
                                                size="small"
                                            />
                                        </div>
                                        <div className="col-span-1 flex items-end pb-1.5">
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => removeDns(index)}
                                            >
                                                <DeleteOutlineIcons />
                                            </IconButton>
                                        </div>
                                    </div>
                                ))}
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    className="w-full border-dashed"
                                    onClick={() =>
                                        appendDns({
                                            name: '',
                                            type: 'cname' as DNSManagementType,
                                            target: '',
                                        })
                                    }
                                >
                                    <Plus className="h-3.5 w-3.5 mr-1" />
                                    {t('add.dns_management')}
                                </Button>
                            </div>
                        </AccordionContent>
                    </AccordionItem>
                )}

                {/* Metadata */}
                <AccordionItem
                    value="metadata"
                    className="border rounded-lg px-4 bg-card"
                >
                    <AccordionTrigger className="py-3 hover:no-underline">
                        <div className="flex items-center gap-2">
                            <Key className="h-4 w-4 text-amber-500" />
                            <span className="font-medium text-sm">
                                {tService('metadata')}
                            </span>
                            {metaFields.length > 0 && (
                                <Badge
                                    variant="outline-primary"
                                    className="h-5 px-1.5 text-xs"
                                >
                                    {metaFields.length}
                                </Badge>
                            )}
                        </div>
                    </AccordionTrigger>
                    <AccordionContent>
                        <div className="space-y-3 pb-1">
                            {/* Quick-add presets */}
                            {availablePresets.length > 0 && (
                                <div className="flex flex-wrap gap-1.5">
                                    <span className="text-xs text-muted-foreground self-center mr-1">
                                        Quick add:
                                    </span>
                                    {availablePresets.map((preset) => (
                                        <Button
                                            key={preset.key}
                                            type="button"
                                            size="sm"
                                            variant="outline"
                                            className="h-6 px-2 text-xs"
                                            onClick={() =>
                                                appendMeta({
                                                    key: preset.key,
                                                    value: '',
                                                })
                                            }
                                        >
                                            <Plus className="h-3 w-3 mr-0.5" />
                                            {preset.label}
                                        </Button>
                                    ))}
                                </div>
                            )}
                            {metaFields.map((field, index) => (
                                <div
                                    key={field.id}
                                    className="grid grid-cols-12 gap-2 items-start"
                                >
                                    <div className="col-span-4">
                                        <FormItem
                                            name={`metadata.${index}.key`}
                                            type="textfield"
                                            control={control}
                                            label="Key"
                                            placeholder="USER / PASS / LINK..."
                                            size="small"
                                        />
                                    </div>
                                    <div className="col-span-7">
                                        <FormItem
                                            name={`metadata.${index}.value`}
                                            type="textfield"
                                            control={control}
                                            label="Value"
                                            placeholder={t(
                                                'service.metadata.value'
                                            )}
                                            size="small"
                                        />
                                    </div>
                                    <div className="col-span-1 flex items-end pb-1.5">
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => removeMeta(index)}
                                        >
                                            <DeleteOutlineIcons />
                                        </IconButton>
                                    </div>
                                </div>
                            ))}
                            <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className="w-full border-dashed"
                                onClick={() =>
                                    appendMeta({ key: '', value: '' })
                                }
                            >
                                <Plus className="h-3.5 w-3.5 mr-1" />
                                {t('add.metadata.item')}
                            </Button>
                        </div>
                    </AccordionContent>
                </AccordionItem>

                {/* Documentation */}
                <AccordionItem
                    value="documentation"
                    className="border rounded-lg px-4 bg-card"
                >
                    <AccordionTrigger className="py-3 hover:no-underline">
                        <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-emerald-500" />
                            <span className="font-medium text-sm">
                                {tService('documentation')}
                            </span>
                            {docFields.length > 0 && (
                                <Badge
                                    variant="outline-info"
                                    className="h-5 px-1.5 text-xs"
                                >
                                    {docFields.length}
                                </Badge>
                            )}
                        </div>
                    </AccordionTrigger>
                    <AccordionContent>
                        <div className="space-y-3 pb-1">
                            {docFields.map((field, index) => (
                                <div
                                    key={field.id}
                                    className="grid grid-cols-12 gap-2 items-start"
                                >
                                    <div className="col-span-4">
                                        <FormItem
                                            name={`documentations.${index}.name`}
                                            type="textfield"
                                            control={control}
                                            label="Name"
                                            placeholder={t(
                                                'service.documentations.name'
                                            )}
                                            size="small"
                                        />
                                    </div>
                                    <div className="col-span-7">
                                        <FormItem
                                            name={`documentations.${index}.link`}
                                            type="textfield"
                                            control={control}
                                            label="Link"
                                            placeholder="https://..."
                                            size="small"
                                        />
                                    </div>
                                    <div className="col-span-1 flex items-end pb-1.5">
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => removeDoc(index)}
                                        >
                                            <DeleteOutlineIcons />
                                        </IconButton>
                                    </div>
                                </div>
                            ))}
                            <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className="w-full border-dashed"
                                onClick={() =>
                                    appendDoc({ name: '', link: '' })
                                }
                            >
                                <Plus className="h-3.5 w-3.5 mr-1" />
                                {t('add.documentation.item')}
                            </Button>
                        </div>
                    </AccordionContent>
                </AccordionItem>

                {/* Attachments */}
                <AccordionItem
                    value="attachments"
                    className="border rounded-lg px-4 bg-card"
                >
                    <AccordionTrigger className="py-3 hover:no-underline">
                        <div className="flex items-center gap-2">
                            <Paperclip className="h-4 w-4 text-purple-500" />
                            <span className="font-medium text-sm">
                                {t('attachments')}
                            </span>
                            {templateAttachments.length > 0 && (
                                <Badge
                                    variant="outline"
                                    className="h-5 px-1.5 text-xs"
                                >
                                    {templateAttachments.length}
                                </Badge>
                            )}
                        </div>
                    </AccordionTrigger>
                    <AccordionContent>
                        <div className="space-y-3 pb-1">
                            <GridHookForm
                                formControl={formControl}
                                subForm
                                actions={[]}
                                data={[
                                    {
                                        formItemProps: {
                                            name: 'attachments',
                                            type: 'files',
                                            renderFileItem: renderFileItem,
                                            multiple: true,
                                            label: t('contract_process_title'),
                                            placeholder: t(
                                                'contract_process_title'
                                            ),
                                        },
                                        className: 'col-span-4',
                                    },
                                ]}
                            />
                            {templateAttachments.length > 0 && (
                                <div className="space-y-1.5">
                                    <Label className="text-xs text-muted-foreground">
                                        Template Attachments:
                                    </Label>
                                    {templateAttachments.map((att, i) => (
                                        <div
                                            key={i}
                                            className="flex items-center gap-2 p-2 rounded bg-muted/30 border text-xs"
                                        >
                                            <Paperclip className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                                            <a
                                                href={att.attachment_file_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-primary hover:underline truncate flex-1"
                                            >
                                                {att.attachment_file_name}
                                            </a>
                                            <span className="text-muted-foreground whitespace-nowrap">
                                                {(
                                                    att.attachment_file_size /
                                                    1024
                                                ).toFixed(1)}{' '}
                                                KB
                                            </span>
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() =>
                                                    setTemplateAttachments(
                                                        (prev) =>
                                                            prev.filter(
                                                                (_, idx) =>
                                                                    idx !== i
                                                            )
                                                    )
                                                }
                                            >
                                                <DeleteOutlineIcons />
                                            </IconButton>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </AccordionContent>
                </AccordionItem>
            </Accordion>

            {/* Footer Actions - sticky */}
            <Separator />
            <div className="flex items-center justify-end gap-3 sticky bottom-0 bg-background pt-2 pb-4">
                <Button
                    type="button"
                    variant="outline"
                    onClick={onClose}
                    disabled={isLoading}
                >
                    <X className="h-4 w-4 mr-1" />
                    {t('cancel')}
                </Button>
                <Button type="submit" disabled={isLoading}>
                    {isLoading ? (
                        <span className="flex items-center gap-2">
                            <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            {isUploading ? 'Uploading...' : 'Saving...'}
                        </span>
                    ) : (
                        <>
                            <Save className="h-4 w-4 mr-1" />
                            {isEditMode
                                ? t('update-notification')
                                : t('create-notification-mailer')}
                        </>
                    )}
                </Button>
            </div>
        </form>
    )
}

export default Create
